/*
 * Monfiora — Build your set (<monfiora-builder>, <monfiora-builder-bar>): the product page block
 * (Phase 1) and the standalone section on the landing page and /pages/build-your-set (Phase 2).
 *
 * Markup and copy: snippets/monfiora-builder.liquid (+ its JSON payload) and
 * snippets/monfiora-builder-bar.liquid, hosted by sections/main-product.liquid or
 * sections/monfiora-build-your-set.liquid. This file holds behaviour only: no customer-facing
 * strings and no amounts. Every amount is formatted with the store's own money format
 * (shop.money_format, from the payload).
 *
 * One module, two hosts (see Hosts below): PdpHost owns Dawn's form and Add to cart; SectionHost
 * owns the section's own CTA [data-mf-cta]. In a section (payload oneMode) there is no current
 * product: Just one opens the same picker with a target of 1 and NOTHING picked ("0 of 1 picked",
 * the card "From {lowest price}", the CTA disabled), and keeps its own pick, oneId, apart from the
 * sets' pickLog; "+" on another scent moves it. Sets start empty there unless the section's
 * prefill_sets is on.
 *
 * Three offers in a fixed order — one (Just one), three (Buy 3 — 5% off), four (Buy 4 + 1 free
 * piece). The state is { option, pickLog, current, freeId, prefillPristine, trim }:
 *   - pickLog is the ordered list of picks, oldest first. "+" appends, "−" removes the LATEST
 *     occurrence of that scent. Only the offer cards change the offer; steppers never do.
 *   - Buy 4 → Buy 3 trims the newest pick (with an Undo); every other switch keeps the picks.
 *   - The landing scent is the only automatic pick (never a sold-out one); after a successful add
 *     the picks reset to it and the offer stays selected.
 *
 * Every option posts ONE /cart/add.js request with an items[] array, via window.fetch looked up at
 * call time (Shopify's analytics listener and lp-free-gift.js both wrap it). One entry per distinct
 * paid scent, without properties, so it merges with any line of that scent already in the cart; the
 * free piece goes last with _free_gift: 'true' (lp-free-gift.js compares it to exactly 'true') and
 * a _set_id unique to this set, so every set's free piece stays a quantity-1 line of its own.
 * Shopify applies a multi-item add all or nothing.
 *
 * On the product page the builder owns Dawn's #ProductSubmitButton-{section} through a document-capture submit listener
 * (it runs before Dawn's own handler and Shopify's form listener, whatever the registration order)
 * and controls it with aria-disabled only, so a tap on an incomplete set reaches the handler and
 * focus never drops to <body>. Buy it now shows for Just one only (CSS keyed on
 * product-info[data-builder-option]); a press that still lands for a set is intercepted here.
 *
 * Runtime partners, read only when the customer acts and feature-checked every time:
 *   window.MonfioraCart     (assets/monfiora-cart.js) sectionIds(), openDrawer(json, {opener, kind}),
 *                           refresh({open, opener, kind, message}), note(text); a rejected
 *                           openDrawer / refresh promise sends the page to the cart instead
 *   window.MonfioraFreeGift (assets/lp-free-gift.js) getCart(), nextState(ms)
 * Without them it falls back to Dawn's drawer element, then to the cart page.
 *
 * Events on document: monfiora:builder:added {option, items, setId, expected} and
 * monfiora:builder:check {result: 'ok'|'mismatch'|'skipped', expected, actual}. Checks and problems
 * are kept in a 20-entry ring in sessionStorage.mf_log.
 *
 * Core (pure, no DOM) is exported as window.MonfioraBuilderCore; tests/monfiora-builder.test.mjs
 * runs this file in node:vm and exercises it directly.
 */
(function () {
  'use strict';

  var root = typeof window !== 'undefined' ? window : typeof globalThis !== 'undefined' ? globalThis : {};

  /* ============================== Core: pure logic ============================== */
  var Core = (function () {
    var OFFERS = {
      one: { target: 1, pct: 0, free: false },
      three: { target: 3, pct: 5, free: false },
      four: { target: 4, pct: 0, free: true }
    };
    var FREE_PROP = '_free_gift';
    var SET_PROP = '_set_id';

    // Integer paise → the store's money format (shop.money_format, e.g. 119415 → "1,194.15" with the
    // store's symbol). Zero uses the label Liquid rendered with money_without_trailing_zeros.
    function formatMoney(paise, format, zeroLabel) {
      var cents = Math.round(Number(paise) || 0);
      if (cents === 0 && zeroLabel) return String(zeroLabel);
      var fmt = String(format || '{{amount}}');
      var re = /\{\{\s*(\w+)\s*\}\}/;
      var m = fmt.match(re);
      function num(decimals, thousands, decimal) {
        var parts = (Math.abs(cents) / 100).toFixed(decimals).split('.');
        var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return (cents < 0 ? '-' : '') + whole + (parts[1] ? decimal + parts[1] : '');
      }
      var map = {
        amount: num(2, ',', '.'),
        amount_no_decimals: num(0, ',', '.'),
        amount_with_comma_separator: num(2, '.', ','),
        amount_no_decimals_with_comma_separator: num(0, '.', ','),
        amount_with_space_separator: num(2, ' ', ','),
        amount_no_decimals_with_space_separator: num(0, ' ', ','),
        amount_with_apostrophe_separator: num(2, "'", '.')
      };
      return m ? fmt.replace(re, map[m[1]] || map.amount) : map.amount;
    }

    // "{n} of {t} picked" + {n: 2, t: 3} → "2 of 3 picked". Unknown keys are left as they are.
    function fill(template, vals) {
      return String(template == null ? '' : template).replace(/\{(\w+)\}/g, function (all, key) {
        return vals && vals[key] != null ? String(vals[key]) : all;
      });
    }

    function mrpOf(v) {
      return Math.max(Number(v.mrp) || 0, Number(v.price) || 0);
    }

    function isAvailable(V, id) {
      var v = id != null ? V[String(id)] : null;
      return !!(v && v.available);
    }

    function firstAvailable(V, ids) {
      for (var i = 0; i < (ids || []).length; i++) if (isAvailable(V, ids[i])) return String(ids[i]);
      return null;
    }

    function counts(pickLog) {
      var map = {};
      (pickLog || []).forEach(function (id) {
        map[id] = (map[id] || 0) + 1;
      });
      return map;
    }

    // What the scent cards show: the Just one pick in a section's Just one (oneMode), else the set.
    function pickCounts(s) {
      if (s.option === 'one' && s.oneMode) {
        var map = {};
        if (s.oneId != null) map[String(s.oneId)] = 1;
        return map;
      }
      return counts(s.pickLog);
    }

    // Price of one offer for the current state.
    //  - Cards show the projected full set: the picks (oldest `target` of them) plus the empty slots
    //    priced as the landing scent.
    //  - picked = the plain sum of the picks themselves ("Total so far").
    //  - Buy 3: 5% off the whole set, rounded to the paisa (ORDER-class discount on all items).
    //  - Buy 4: the 4 paid pieces in full; the free piece is 0 and its MRP joins the struck total,
    //    so "You save" here equals the cart's.
    //  - Just one: the landing scent on the product page; in a section (oneMode) its own pick, oneId
    //    (incomplete while nothing is picked; the card then reads "From" fromPrice() instead).
    function quote(V, option, s) {
      var offer = OFFERS[option];
      var current = s.current != null ? V[String(s.current)] : null;
      var ids;
      if (option === 'one' && s.oneMode) ids = s.oneId != null && V[String(s.oneId)] ? [String(s.oneId)] : [];
      else ids = option === 'one' ? (current ? [String(s.current)] : []) : (s.pickLog || []).slice(0, offer.target);
      var picked = 0;
      var mrpTotal = 0;
      ids.forEach(function (id) {
        var v = V[id];
        if (!v) return;
        picked += v.price;
        mrpTotal += mrpOf(v);
      });
      var count = ids.length;
      var fillCount = Math.max(0, offer.target - count);
      var total = picked;
      if (fillCount && current) {
        total += current.price * fillCount;
        mrpTotal += mrpOf(current) * fillCount;
      }
      var discount = offer.pct ? Math.round((total * offer.pct) / 100) : 0;
      total -= discount;
      if (offer.free && s.freeId != null && V[String(s.freeId)]) mrpTotal += mrpOf(V[String(s.freeId)]);
      var oneComplete = s.oneMode ? !!(ids.length && V[ids[0]].available) : !!(current && current.available);
      return {
        option: option,
        target: offer.target,
        count: count,
        complete: option === 'one' ? oneComplete : count === offer.target,
        picked: picked,
        total: total,
        mrpTotal: mrpTotal,
        save: mrpTotal - total,
        discount: discount,
        projected: fillCount > 0
      };
    }

    // The add body's items: one entry per distinct paid scent (first-pick order, no properties),
    // then the free piece last with its own properties.
    function buildItems(s, setId) {
      if (s.option === 'one' && s.oneMode) return s.oneId != null ? [{ id: Number(s.oneId), quantity: 1 }] : [];
      if (s.option === 'one') return s.current != null ? [{ id: Number(s.current), quantity: 1 }] : [];
      var order = [];
      var qty = {};
      (s.pickLog || []).forEach(function (id) {
        id = String(id);
        if (!qty[id]) {
          qty[id] = 0;
          order.push(id);
        }
        qty[id] += 1;
      });
      var items = order.map(function (id) {
        return { id: Number(id), quantity: qty[id] };
      });
      if (s.option === 'four' && s.freeId != null) {
        var props = {};
        props[FREE_PROP] = 'true';
        props[SET_PROP] = setId;
        items.push({ id: Number(s.freeId), quantity: 1, properties: props });
      }
      return items;
    }

    // A section's Just one (oneMode) is single-choice: "+" on another scent replaces the pick.
    function addPick(V, s, id) {
      if (s.option === 'one' && s.oneMode) {
        id = String(id);
        if (!isAvailable(V, id)) return 'soldout';
        if (s.oneId != null && String(s.oneId) === id) return 'full';
        var had = s.oneId != null;
        s.oneId = id;
        s.trim = null;
        return had ? 'replaced' : 'added';
      }
      if (s.option === 'one') return 'off';
      id = String(id);
      if (!isAvailable(V, id)) return 'soldout';
      if (s.pickLog.length >= OFFERS[s.option].target) return 'full';
      s.pickLog.push(id);
      s.prefillPristine = false;
      s.trim = null;
      return 'added';
    }

    function removePick(s, id) {
      if (s.option === 'one' && s.oneMode) {
        if (s.oneId == null || String(s.oneId) !== String(id)) return 'none';
        s.oneId = null;
        return 'removed';
      }
      var i = s.pickLog.lastIndexOf(String(id));
      if (i < 0) return 'none';
      s.pickLog.splice(i, 1);
      s.prefillPristine = false;
      s.trim = null;
      return 'removed';
    }

    // Switch offers. Picks are kept; going over the new target trims the newest pick(s) and
    // remembers them for Undo. Returns the trimmed ids, or null.
    function switchOffer(s, to) {
      if (!OFFERS[to]) return null;
      s.option = to;
      s.trim = null;
      if (to === 'one') return null;
      var target = OFFERS[to].target;
      if (s.pickLog.length <= target) return null;
      var removed = s.pickLog.splice(target);
      s.trim = { ids: removed };
      return removed;
    }

    // Undo a trim: back to Buy 4 with the pick restored.
    function undoTrim(s) {
      if (!s.trim) return false;
      s.option = 'four';
      s.pickLog = s.pickLog.concat(s.trim.ids).slice(0, OFFERS.four.target);
      s.trim = null;
      return true;
    }

    // Back to the start: the landing scent on the product page. In a section (oneMode) the sets
    // start empty unless prefillSets, and Just one has nothing picked: the customer chooses.
    function resetPicks(V, s) {
      var cur = isAvailable(V, s.current) ? String(s.current) : null;
      if (s.oneMode) {
        s.pickLog = s.prefillSets && cur ? [cur] : [];
        s.oneId = null;
      } else {
        s.pickLog = cur ? [cur] : [];
      }
      s.prefillPristine = true;
      s.trim = null;
    }

    // "From {money}" on a section's Just one card while nothing is picked: the lowest price among
    // the picker's scents in stock (ids = the payload's order), else the lowest listed, else 0.
    function fromPrice(V, ids) {
      var best = null;
      var any = null;
      (ids || []).forEach(function (id) {
        var v = V[String(id)];
        if (!v) return;
        if (any == null || v.price < any) any = v.price;
        if (v.available && (best == null || v.price < best)) best = v.price;
      });
      return best != null ? best : any != null ? any : 0;
    }

    // The fragrance tiles changed the landing scent (Just one). While the picks are still the
    // untouched pre-fill, the pre-fill follows it; otherwise picks are never changed silently.
    function moveCurrent(V, s, id) {
      s.current = String(id);
      if (s.prefillPristine) s.pickLog = isAvailable(V, s.current) ? [s.current] : [];
    }

    // Scents Shopify refused as sold out: flip them, drop their picks, re-choose the free piece.
    function markSoldOut(V, s, pool, ids) {
      var paid = [];
      var free = null;
      (ids || []).forEach(function (id) {
        id = String(id);
        if (V[id]) V[id].available = false;
        if (s.pickLog.indexOf(id) !== -1) {
          paid.push(id);
          s.pickLog = s.pickLog.filter(function (x) {
            return x !== id;
          });
          s.prefillPristine = false;
        }
        if (s.oneId != null && String(s.oneId) === id) {
          s.oneId = null;
          if (paid.indexOf(id) === -1) paid.push(id);
        }
      });
      if (s.freeId != null && !isAvailable(V, s.freeId)) {
        free = { from: String(s.freeId), to: firstAvailable(V, pool) };
        s.freeId = free.to;
      }
      s.trim = null;
      return { paid: paid, free: free };
    }

    // What the add response means. 'lost' (no response at all) is decided by the caller.
    function classify(status, ok, json, unreadable) {
      if (status === 429) return 'rate_limited';
      if (status === 422 || (json && Number(json.status) === 422)) return 'refused';
      if (ok && json && Array.isArray(json.items)) return 'ok';
      if (ok && unreadable) return 'ok_unreadable';
      return 'failed';
    }

    // What the builder does with an add outcome:
    //   'success'   the response carries the cart lines: open the drawer from it;
    //   'committed' a 2xx whose body could not be read: show the cart from the server;
    //   'refused'   a 422: explain the sold-out pick, nothing was added;
    //   'check'     the add may or may not have been saved, so read the cart before saying anything
    //               (resolveLost): no response at all, or a 5xx / status-0 reply — Shopify can save the
    //               set and still answer 500 (seen on the preview, 4 Oct 2026), and "Nothing was
    //               added" would invite a second tap that doubles the set;
    //   'error'     nothing was saved: a 429, or any other 4xx (a challenge page never reached the
    //               cart).
    function nextStep(outcome) {
      var type = outcome && outcome.type;
      if (type === 'ok') return 'success';
      if (type === 'ok_unreadable') return 'committed';
      if (type === 'refused') return 'refused';
      if (type === 'lost') return 'check';
      var status = Number(outcome && outcome.status) || 0;
      if (type === 'failed' && (status === 0 || status >= 500)) return 'check';
      return 'error';
    }

    // POST the add. fetchFn(url, init) → Promise<Response-like>. Resolves {type, json, status}.
    function submit(fetchFn, url, body, timeoutMs) {
      var controller = typeof AbortController === 'function' ? new AbortController() : null;
      var timer = null;
      var init = {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(body)
      };
      if (controller) {
        init.signal = controller.signal;
        timer = setTimeout(function () {
          controller.abort();
        }, timeoutMs || 15000);
      }
      var done = function (result) {
        if (timer) clearTimeout(timer);
        return result;
      };
      var request;
      try {
        request = Promise.resolve(fetchFn(url, init));
      } catch (e) {
        request = Promise.reject(e);
      }
      return request
        .then(function (res) {
          return Promise.resolve(res.text()).then(function (text) {
            var json = null;
            var unreadable = false;
            try {
              json = text ? JSON.parse(text) : null;
            } catch (e) {
              unreadable = true;
            }
            if (!json && !unreadable) unreadable = true;
            return { type: classify(res.status, !!res.ok, json, unreadable), json: json, status: res.status };
          });
        })
        .then(done, function () {
          return done({ type: 'lost', json: null, status: 0 });
        });
    }

    // A read that always settles: the parsed JSON, or null on any failure or after timeoutMs (the
    // race also covers a fetch that ignores the abort), so no busy state waits on a stalled network.
    function getJson(fetchFn, url, timeoutMs) {
      var controller = typeof AbortController === 'function' ? new AbortController() : null;
      var timer = null;
      var init = { headers: { Accept: 'application/json' }, credentials: 'same-origin' };
      if (controller) init.signal = controller.signal;
      var request;
      try {
        request = Promise.resolve(fetchFn(url, init));
      } catch (e) {
        request = Promise.reject(e);
      }
      var read = request
        .then(function (res) {
          return res && res.ok ? res.json() : null;
        })
        .catch(function () {
          return null;
        });
      var late = new Promise(function (resolve) {
        timer = setTimeout(function () {
          if (controller) controller.abort();
          resolve(null);
        }, timeoutMs || 8000);
      });
      return Promise.race([read, late]).then(function (result) {
        clearTimeout(timer);
        return result == null ? null : result;
      });
    }

    // Shopify's 422 description names the product and variant ("<Product> - <Variant> …").
    function matchSoldOut(V, description, ids) {
      var d = String(description || '').toLowerCase();
      if (!d) return [];
      var hits = [];
      (ids || []).forEach(function (id) {
        id = String(id);
        var v = V[id];
        if (!v || hits.indexOf(id) !== -1) return;
        var keys = [v.pt && v.vt ? v.pt + ' - ' + v.vt : '', v.design && v.scent ? v.design + ' - ' + v.scent : ''];
        for (var i = 0; i < keys.length; i++) {
          if (keys[i] && d.indexOf(keys[i].toLowerCase()) !== -1) {
            hits.push(id);
            return;
          }
        }
      });
      return hits;
    }

    // Shopify's limited-stock 422 ("You can only add 2 of <product> - <variant> …"): the number that
    // still fits, or null when the description carries none (then it reads as sold out, Phase 1).
    function parseLimit(description) {
      var m = String(description || '').match(/\bonly add (\d+)\b/i);
      return m ? Number(m[1]) : null;
    }

    // Fallback when the description names nothing: re-read each picked design (at most 5,
    // one after another, error path only) and take the posted variants it reports sold out.
    // getProduct(url) → Promise<{variants: [{id, available}]}|null>.
    function findSoldOut(V, ids, getProduct) {
      var groups = [];
      var byUrl = {};
      (ids || []).forEach(function (id) {
        id = String(id);
        var v = V[id];
        if (!v || !v.u) return;
        if (!byUrl[v.u]) {
          if (groups.length >= 5) return;
          byUrl[v.u] = [];
          groups.push(v.u);
        }
        if (byUrl[v.u].indexOf(id) === -1) byUrl[v.u].push(id);
      });
      var hits = [];
      return groups
        .reduce(function (chain, url) {
          return chain.then(function () {
            var request;
            try {
              request = Promise.resolve(getProduct(url));
            } catch (e) {
              request = Promise.resolve(null);
            }
            return request.then(
              function (product) {
                var variants = (product && product.variants) || [];
                byUrl[url].forEach(function (id) {
                  for (var i = 0; i < variants.length; i++) {
                    if (String(variants[i].id) === id && variants[i].available === false) hits.push(id);
                  }
                });
              },
              function () {}
            );
          });
        }, Promise.resolve())
        .then(function () {
          return hits;
        });
    }

    function isFreeLine(line) {
      return !!(line && line.properties && line.properties[FREE_PROP] === 'true');
    }

    function paidQty(cart, id) {
      var n = 0;
      ((cart && cart.items) || []).forEach(function (line) {
        if (String(line.variant_id) === String(id) && !isFreeLine(line)) n += Number(line.quantity) || 0;
      });
      return n;
    }

    // The add's response never arrived. Did Shopify commit it?
    //  - Buy 4: its free line carries this set's _set_id — present means committed, absent means
    //    nothing was added (the add is all or nothing).
    //  - Just one / Buy 3: compare with the cart read before the add, when there is one; with none,
    //    a cart holding none of the posted scents proves nothing was added.
    // Returns 'found' | 'absent' | 'unknown'.
    function resolveLost(opts) {
      var after = opts.after;
      if (!after || !Array.isArray(after.items)) return 'unknown';
      if (opts.option === 'four' && opts.setId) {
        var found = after.items.some(function (line) {
          return !!(line.properties && line.properties[SET_PROP] === opts.setId);
        });
        return found ? 'found' : 'absent';
      }
      var paid = (opts.items || []).filter(function (i) {
        return !i.properties;
      });
      if (!paid.length) return 'unknown';
      var before = opts.before;
      if (before && Array.isArray(before.items)) {
        var grewAll = paid.every(function (i) {
          return paidQty(after, i.id) - paidQty(before, i.id) >= i.quantity;
        });
        if (grewAll) return 'found';
        var grewNone = paid.every(function (i) {
          return paidQty(after, i.id) <= paidQty(before, i.id);
        });
        return grewNone ? 'absent' : 'unknown';
      }
      var none = paid.every(function (i) {
        return paidQty(after, i.id) === 0;
      });
      return none ? 'absent' : 'unknown';
    }

    // After a successful add: compare the cart with what the button promised — but only when the
    // cart holds exactly the set just posted. With other lines in it, the one offer per order is
    // worked out over the whole cart, so only invariants are checked (and logged, never shown).
    function checkCart(items, cart, expected, setId, option) {
      var out = { result: 'skipped', expected: expected, actual: null, notes: [] };
      if (!cart || !Array.isArray(cart.items)) {
        out.notes.push('no_cart');
        return out;
      }
      out.actual = cart.total_price;
      var posted = {};
      (items || []).forEach(function (i) {
        var key = i.id + '|' + (i.properties && i.properties[FREE_PROP] === 'true' ? 1 : 0);
        posted[key] = (posted[key] || 0) + i.quantity;
      });
      var inCart = {};
      cart.items.forEach(function (line) {
        var key = line.variant_id + '|' + (isFreeLine(line) ? 1 : 0);
        inCart[key] = (inCart[key] || 0) + (Number(line.quantity) || 0);
      });
      var keys = Object.keys(posted);
      var clean =
        keys.length === Object.keys(inCart).length &&
        keys.every(function (k) {
          return inCart[k] === posted[k];
        });
      if (clean) {
        out.result = cart.total_price === expected ? 'ok' : 'mismatch';
        return out;
      }
      keys.forEach(function (k) {
        if ((inCart[k] || 0) < posted[k]) out.notes.push('add_incomplete');
      });
      if (option === 'four' && setId) {
        var line = cart.items.filter(function (l) {
          return l.properties && l.properties[SET_PROP] === setId;
        })[0];
        if (!line || Number(line.final_line_price) !== 0) out.notes.push('free_not_applied');
      }
      if (option === 'three' && !(cart.cart_level_discount_applications || []).length) out.notes.push('offer_recalculated');
      return out;
    }

    function newSetId() {
      return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
    }

    return {
      OFFERS: OFFERS,
      FREE_PROP: FREE_PROP,
      formatMoney: formatMoney,
      fill: fill,
      mrpOf: mrpOf,
      isAvailable: isAvailable,
      firstAvailable: firstAvailable,
      counts: counts,
      pickCounts: pickCounts,
      quote: quote,
      buildItems: buildItems,
      addPick: addPick,
      removePick: removePick,
      switchOffer: switchOffer,
      undoTrim: undoTrim,
      resetPicks: resetPicks,
      fromPrice: fromPrice,
      moveCurrent: moveCurrent,
      markSoldOut: markSoldOut,
      classify: classify,
      nextStep: nextStep,
      submit: submit,
      getJson: getJson,
      matchSoldOut: matchSoldOut,
      parseLimit: parseLimit,
      findSoldOut: findSoldOut,
      resolveLost: resolveLost,
      checkCart: checkCart,
      newSetId: newSetId
    };
  })();

  root.MonfioraBuilderCore = Core;

  if (typeof HTMLElement === 'undefined' || typeof customElements === 'undefined') return;

  /* ============================== Page helpers ============================== */
  var BUY_NOW_HOSTS = 'magic-checkout-btn, .magic-buy-btn__parent, .magic-buy-btn, .shopify-payment-button';
  var OPTIONS = ['one', 'three', 'four'];
  // The amount on the CTA: the section's own slot, or Dawn's (snippets/buy-buttons.liquid).
  var PRICE_SLOT = '[data-mf-cta-price], [data-tier-cta-price]';
  // Cart and product reads on the add's error paths give up after this, so busy always ends.
  var READ_TIMEOUT = 8000;

  // Read at call time, so a fetch wrapped after this script loaded (analytics, Razorpay) is used.
  function windowFetch(url, init) {
    return window.fetch(url, init);
  }

  // Dawn's pub/sub lives in lexical globals (const PUB_SUB_EVENTS, function subscribe), so it is
  // read by name; window.* guards alone would always be empty.
  function subscribeEvent(name, callback) {
    /* global PUB_SUB_EVENTS, subscribe */
    var events = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : window.PUB_SUB_EVENTS;
    var fn = typeof subscribe === 'function' ? subscribe : window.subscribe;
    if (events && events[name] && typeof fn === 'function') return fn(events[name], callback);
    return null;
  }

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  // The height of a header that stays pinned while the page scrolls down (Dawn's sticky types
  // "always" and "reduce-logo-size": 66px, 98px from 990px), read from the server-rendered
  // data-sticky-type so it holds before the header's own script runs. 0 for a header that scrolls
  // away or hides on the way down: then nothing covers the top of the screen.
  function stickyHeaderHeight() {
    var sticky = document.querySelector('sticky-header[data-sticky-type]');
    var type = sticky ? sticky.getAttribute('data-sticky-type') : '';
    if (type !== 'always' && type !== 'reduce-logo-size') return 0;
    var header = (sticky.closest && sticky.closest('.section-header')) || sticky;
    var height = header.offsetHeight;
    return height > 0 ? Math.round(height) : 0;
  }

  function emit(name, detail) {
    try {
      document.dispatchEvent(new CustomEvent(name, { detail: detail }));
    } catch (e) {}
  }

  // A 20-entry ring in sessionStorage.mf_log (QA reads it); problems also go to the console.
  function log(entry, warn) {
    entry.src = 'builder';
    entry.t = Date.now();
    try {
      var raw = window.sessionStorage.getItem('mf_log');
      var list = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(list)) list = [];
      list.push(entry);
      while (list.length > 20) list.shift();
      window.sessionStorage.setItem('mf_log', JSON.stringify(list));
    } catch (e) {}
    if (warn) console.warn('[monfiora-builder]', entry);
  }

  function setText(el, text) {
    if (el && el.textContent !== text) el.textContent = text;
  }

  function setFlag(el, name, on) {
    if (!el) return;
    if (on) {
      if (!el.hasAttribute(name)) el.setAttribute(name, '');
    } else if (el.hasAttribute(name)) {
      el.removeAttribute(name);
    }
  }

  function setAria(el, name, value) {
    if (!el) return;
    if (value == null || value === false) {
      if (el.hasAttribute(name)) el.removeAttribute(name);
    } else if (el.getAttribute(name) !== String(value)) {
      el.setAttribute(name, String(value));
    }
  }

  // Fill an element from a template whose placeholders may be nodes (money spans, a bold name).
  // Rebuilt only when the resulting text changes.
  function put(el, template, vals) {
    if (!el) return;
    var textVals = {};
    Object.keys(vals || {}).forEach(function (k) {
      var v = vals[k];
      textVals[k] = v && v.nodeType ? v.textContent : v;
    });
    var text = Core.fill(template, textVals);
    if (el.__mfText === text && el.textContent === text) return;
    el.__mfText = text;
    while (el.firstChild) el.removeChild(el.firstChild);
    var re = /\{(\w+)\}/g;
    var last = 0;
    var m;
    var src = String(template || '');
    while ((m = re.exec(src))) {
      if (m.index > last) el.appendChild(document.createTextNode(src.slice(last, m.index)));
      var v = vals ? vals[m[1]] : null;
      if (v && v.nodeType) el.appendChild(v);
      else el.appendChild(document.createTextNode(v != null ? String(v) : m[0]));
      last = re.lastIndex;
    }
    if (last < src.length) el.appendChild(document.createTextNode(src.slice(last)));
  }

  function span(cls, text, tag) {
    var el = document.createElement(tag || 'span');
    if (cls) el.className = cls;
    if (text != null) el.textContent = text;
    return el;
  }

  // A design or scent name: product names, kept out of page machine translation (as in the Liquid).
  function nameSpan(cls, text) {
    var el = span(cls, text);
    el.setAttribute('translate', 'no');
    return el;
  }

  /* ============================== Hosts ============================== */
  // Everything the builder touches outside its own subtree goes through its host, picked from
  // data-context on <monfiora-builder> (written by the snippet):
  //   PdpHost      the product page: Dawn's form, #ProductSubmitButton-{section}, product-info, Buy
  //                it now and the variant picker; the bar sits inside product-info.
  //   SectionHost  sections/monfiora-build-your-set.liquid: its own CTA [data-mf-cta] and root
  //                .monfiora-bys, no Dawn form and no product-info; the bar is the root's sibling.
  // Each method takes the element, so a host holds no state of its own.
  var PdpHost = {
    kind: 'pdp',
    barOne: 'variant', // Just one in the bar: the "Fragrance: {Scent}" button
    needsReach: false,
    selectEvent: 'shopify:block:select',
    setup: function (el) {
      el.info = el.closest('product-info');
      el.root = null;
      el.form = document.getElementById(el.getAttribute('data-form'));
      var button = document.getElementById('ProductSubmitButton-' + el.sid);
      el.button = button && el.info && el.info.contains(button) ? button : null;
      el.bar = el.info ? el.info.querySelector('monfiora-builder-bar[data-section="' + el.sid + '"]') : null;
    },
    // Capture on the document so this runs before Dawn's submit handler on the form and Shopify's
    // form listener, whatever order they registered in (main button, bar, keyboard).
    bindCommit: function (el, commit) {
      var onSubmit = function (event) {
        if (!el.form || event.target !== el.form) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        commit(event.submitter || null);
      };
      document.addEventListener('submit', onSubmit, true);
      return function () {
        document.removeEventListener('submit', onSubmit, true);
      };
    },
    // Buy it now never carries a set (one id, one quantity): hidden for Buy 3 / Buy 4 by CSS, and
    // a press that still lands is caught here. pointerdown / touchend are only stopped, never
    // cancelled (cancelling touchend would suppress the click); the click decides.
    bindBuyNow: function (el, commit) {
      var onBuyNow = function (event) {
        var host = el.buyNowHost(event);
        if (!host || !el.info || !el.info.contains(host)) return;
        var s = el.state;
        var blocked = s.option !== 'one';
        var soldOut = s.option === 'one' && !Core.isAvailable(el.V, s.current);
        if (!blocked && !soldOut && !el.busy) return;
        event.stopImmediatePropagation();
        if (event.type !== 'click') return;
        event.preventDefault();
        if (soldOut || el.busy) return;
        commit(null);
      };
      ['pointerdown', 'touchend', 'click'].forEach(function (type) {
        document.addEventListener(type, onBuyNow, { capture: true, passive: type !== 'click' });
      });
      return function () {
        ['pointerdown', 'touchend', 'click'].forEach(function (type) {
          document.removeEventListener(type, onBuyNow, { capture: true });
        });
      };
    },
    subscribeVariant: function (el) {
      return subscribeEvent('variantChange', function (event) {
        var data = (event && event.data) || {};
        var variant = data.variant;
        // Dawn sends every product's change to every subscriber; product-info has just written the
        // new id into this form, so any other id is another product (a quick-add modal).
        var idInput = el.form && el.form.querySelector('input[name="id"]');
        if (!variant || (idInput && String(idInput.value) !== String(variant.id))) return;
        el.onVariant(variant);
      });
    },
    isSelected: function (el, event) {
      return event.target === el;
    },
    // Under the buy row (Dawn's Add to cart), as an alert.
    showError: function (el, message) {
      var row = el.button && el.button.closest('.monfiora-pdp-buy-row');
      var anchor = row || (el.button && el.button.closest('product-form'));
      if (!anchor || !anchor.parentNode) return;
      // The builder's own class only: lpfg's clearHoldError() removes every .lpfg-hold-error.
      var p = document.createElement('p');
      p.className = 'monfiora-builder__error';
      p.setAttribute('role', 'alert');
      p.textContent = message;
      anchor.parentNode.insertBefore(p, anchor.nextSibling);
      el.errorEl = p;
    },
    clearError: function (el) {
      if (el.errorEl && el.errorEl.parentNode) el.errorEl.parentNode.removeChild(el.errorEl);
      el.errorEl = null;
    }
  };

  var SectionHost = {
    kind: 'section',
    barOne: 'text', // Just one in the bar: the offer name and the pick, no variant button
    needsReach: true,
    selectEvent: 'shopify:section:select',
    setup: function (el) {
      el.info = null;
      el.form = null;
      el.root = el.closest('.monfiora-bys');
      el.button = el.querySelector('[data-mf-cta]');
      var scope = el.root && el.root.parentNode;
      el.bar = scope ? scope.querySelector('monfiora-builder-bar[data-section="' + el.sid + '"]') : null;
      el.errorBox = el.querySelector('[data-mf-error]');
    },
    // The section's own CTA and the bar's (both type="button"): a press commits.
    bindCommit: function (el, commit) {
      var targets = [el.button, el.bar ? el.bar.querySelector('[data-bar-cta]') : null].filter(Boolean);
      var onClick = function (event) {
        commit(event.currentTarget || null);
      };
      targets.forEach(function (t) {
        t.addEventListener('click', onClick);
      });
      return function () {
        targets.forEach(function (t) {
          t.removeEventListener('click', onClick);
        });
      };
    },
    bindBuyNow: function () {
      return null;
    },
    subscribeVariant: function () {
      return null;
    },
    isSelected: function (el, event) {
      return !!(event.detail && String(event.detail.sectionId) === String(el.sid));
    },
    // Written into the checkout block's own alert line, next to the button that caused it.
    showError: function (el, message) {
      setText(el.errorBox, message);
    },
    clearError: function (el) {
      setText(el.errorBox, '');
    },
    // No JS-free Dawn form to fall back to: the builder hides and the design links show.
    fail: function (el) {
      var root = el.root || el.closest('.monfiora-bys');
      if (root) {
        root.removeAttribute('data-builder-ready');
        root.setAttribute('data-builder-option', 'off');
        var fallback = root.querySelector('[data-bys-fallback]');
        if (fallback) fallback.hidden = false;
      }
      if (el.bar) el.bar.hidden = true;
      setFlag(document.documentElement, 'data-mf-builder-reached', false);
    }
  };

  /* ============================== <monfiora-builder> ============================== */
  class MonfioraBuilder extends HTMLElement {
    // Picked once, from the snippet's data-context; anything else is the product page.
    get host() {
      if (!this._host) this._host = this.getAttribute('data-context') === 'section' ? SectionHost : PdpHost;
      return this._host;
    }

    connectedCallback() {
      // Quick add injects the whole product-info into a modal: the modal is Dawn's plain single add.
      // (A section is never inside product-info, so it never reaches a modal.)
      if (this.host.kind === 'pdp' && this.closest('quick-add-modal')) {
        var modalInfo = this.closest('product-info');
        if (modalInfo) modalInfo.setAttribute('data-builder-option', 'off');
        this.remove();
        return;
      }
      if (this.bound || this.failed) return;
      try {
        if (!this.state) this.setup();
        this.bind();
        this.render();
        this.setAttribute('data-builder-ready', '');
        if (this.info) this.info.setAttribute('data-builder-ready', '');
        if (this.root) this.root.setAttribute('data-builder-ready', '');
        // Only now does the builder own the button: the label kept for the fallback can go.
        if (this.button) this.button.removeAttribute('data-mf-preboot');
      } catch (e) {
        console.error('[monfiora-builder]', e);
        this.fail();
      }
    }

    // Boot failed part-way (or on a re-connect): hand the page back to Dawn's own form at once. The
    // snippet's inline fallback runs only once, at DOMContentLoaded, so it cannot be relied on here,
    // and a page must never be left with a set label and aria-disabled on its only Add to cart.
    fail() {
      this.failed = true;
      try {
        this.unbind();
      } catch (e) {}
      this.removeAttribute('data-builder-ready');
      this.removeAttribute('aria-busy');
      if (this.host.fail) {
        this.host.fail(this);
        return;
      }
      var info = this.info || this.closest('product-info');
      if (info) {
        info.removeAttribute('data-builder-ready');
        info.setAttribute('data-builder-option', 'off');
      }
      var b = this.button || document.getElementById('ProductSubmitButton-' + this.getAttribute('data-section'));
      if (!b || (info && !info.contains(b))) return;
      var available = this.state && this.V ? Core.isAvailable(this.V, this.state.current) : null;
      var strings = window.variantStrings || {};
      var text = available === true ? strings.addToCart : available === false ? strings.soldOut : '';
      text = text || b.getAttribute('data-mf-preboot') || this.dawnLabel || '';
      var label = b.querySelector('span');
      if (label && text) label.textContent = text;
      b.removeAttribute('data-mf-preboot');
      b.removeAttribute('aria-disabled');
      b.removeAttribute('aria-busy');
      b.classList.remove('loading');
      if (available != null) b.disabled = !available;
      else if (this.dawnDisabled != null) b.disabled = this.dawnDisabled;
      var spinner = b.querySelector('.loading__spinner');
      if (spinner) spinner.classList.add('hidden');
      var price = b.querySelector(PRICE_SLOT);
      if (price) {
        price.textContent = '';
        price.hidden = true;
      }
      if (this.describedBy) {
        var ids = (b.getAttribute('aria-describedby') || '').split(/\s+/).filter(function (id) {
          return id && id !== this.describedBy;
        }, this);
        if (ids.length) b.setAttribute('aria-describedby', ids.join(' '));
        else b.removeAttribute('aria-describedby');
      }
    }

    disconnectedCallback() {
      this.unbind();
    }

    setup() {
      var self = this;
      this.sid = this.getAttribute('data-section');
      var payload = document.getElementById(this.getAttribute('data-payload'));
      this.data = JSON.parse(payload.textContent);
      this.text = this.data.text || {};
      this.V = {};
      Object.keys(this.data.variants || {}).forEach(function (id) {
        var v = self.data.variants[id];
        self.V[String(id)] = {
          h: v.h,
          u: v.u,
          design: v.design,
          scent: v.scent,
          ultra: !!v.ultra,
          price: Number(v.price) || 0,
          mrp: Number(v.mrp) || 0,
          available: !!v.available,
          pt: v.pt,
          vt: v.vt
        };
      });
      this.pool = (this.data.freePool || []).map(String);
      this.host.setup(this);
      this.cartCount = Number(this.data.cartCount) || 0;

      this.radios = {};
      this.labels = {};
      OPTIONS.forEach(function (opt) {
        self.radios[opt] = self.querySelector('#MfOffer-' + opt + '-' + self.sid);
        self.labels[opt] = self.querySelector('.monfiora-option[data-option="' + opt + '"]');
      });
      this.els = {
        fine: this.querySelector('[data-fine]'),
        picker: this.querySelector('[data-picker]'),
        pickLabel: this.querySelector('#MfPickLabel-' + this.sid),
        // Every copy: a section mirrors the counter and the tray in its desktop rail.
        countTexts: Array.prototype.slice.call(this.querySelectorAll('[data-count-text]')),
        countChecks: Array.prototype.slice.call(this.querySelectorAll('[data-count-check]')),
        live: this.querySelector('[data-live]'),
        trays: Array.prototype.slice.call(this.querySelectorAll('[data-tray]')),
        helper: this.querySelector('[data-helper]'),
        // A section's rail: its head label ("Your set", "Your pick" in Just one) and, in Just one,
        // the pick by name under it (desktop only, by CSS).
        railLabel: this.querySelector('[data-rail-label]'),
        railPick: this.querySelector('[data-rail-pick]'),
        railPickName: this.querySelector('[data-rail-pick-name]'),
        notice: this.querySelector('[data-notice]'),
        noticeText: this.querySelector('[data-notice-text]'),
        free: this.querySelector('[data-freepick]'),
        freeThumb: this.querySelector('[data-free-thumb]'),
        freeName: this.querySelector('[data-free-name]'),
        freeDesign: this.querySelector('[data-free-design]'),
        freeMrp: this.querySelector('[data-free-mrp]'),
        freeToggle: this.querySelector('[data-free-toggle]'),
        freePanel: this.querySelector('[data-free-panel]'),
        summary: this.querySelector('[data-summary]'),
        sofarRow: this.querySelector('[data-summary-sofar]'),
        sofar: this.querySelector('[data-sofar]'),
        totals: this.querySelector('[data-summary-total]'),
        totalMrp: this.querySelector('[data-total-mrp]'),
        total: this.querySelector('[data-total]'),
        totalSave: this.querySelector('[data-total-save]'),
        totalIncl: this.querySelector('[data-total-incl]'),
        alert: this.querySelector('[data-alert]'),
        offers: this.querySelector('.monfiora-builder__step--offers')
      };
      var fourSub = this.labels.four && this.labels.four.querySelector('[data-option-sub]');
      this.fourSub = fourSub ? fourSub.textContent.trim() : '';
      this.cards = {};
      Array.prototype.forEach.call(this.querySelectorAll('.monfiora-scent[data-variant-id]'), function (card) {
        self.cards[card.getAttribute('data-variant-id')] = card;
      });
      this.groups = Array.prototype.slice.call(this.querySelectorAll('.monfiora-builder__group'));
      this.freeInputs = Array.prototype.slice.call(this.querySelectorAll('input[name="mf-free-' + this.sid + '"]'));

      var checked = this.querySelector('input[name="mf-offer-' + this.sid + '"]:checked');
      var option = (checked && !checked.disabled && checked.value) || this.data.defaultOffer || 'four';
      var current = this.data.current != null ? String(this.data.current) : null;
      var checkedFree = this.freeInputs.filter(function (i) {
        return i.checked && !i.disabled;
      })[0];
      this.state = {
        option: Core.OFFERS[option] ? option : 'four',
        pickLog: [],
        current: current,
        freeId: checkedFree ? String(checkedFree.value) : this.data.freeDefault != null ? String(this.data.freeDefault) : Core.firstAvailable(this.V, this.pool),
        prefillPristine: true,
        trim: null
      };
      if (this.data.oneMode) {
        // A section: Just one keeps its own pick (oneId, none to start), apart from the sets'.
        this.state.oneMode = true;
        this.state.oneId = null;
        this.state.prefillSets = !!this.data.prefillSets;
      }
      Core.resetPicks(this.V, this.state);
      if (this.state.option === 'four' && !this.freeAvailable()) this.state.option = 'three';
      this.busy = false;
      this.added = false;
      this.seen = false;
      this.ctaVisible = true;
      this.offersVisible = false;
      this.reached = false;
      this.barError = null;

      if (this.button && this.host.kind === 'pdp') {
        // Dawn's own label and state, for fail(); data-mf-preboot stays until boot completes.
        var dawnSpan = this.button.querySelector('span');
        this.dawnLabel = this.button.getAttribute('data-mf-preboot') || (dawnSpan ? dawnSpan.textContent.trim() : '');
        this.dawnDisabled = this.button.disabled;
        var summaryId = this.els.summary && this.els.summary.id;
        var described = (this.button.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
        if (summaryId && described.indexOf(summaryId) === -1) {
          described.push(summaryId);
          this.button.setAttribute('aria-describedby', described.join(' '));
          this.describedBy = summaryId;
        }
      }
      // The server already rendered the tray and the free piece for this state: keep those nodes
      // (and their small images) on the first render instead of rebuilding them from card clones.
      var serverCur = this.V[String(this.data.current)];
      var serverFree = this.data.freeDefault != null ? String(this.data.freeDefault) : '';
      var serverOffer = this.data.defaultOffer;
      // A section's Just one renders its one-slot tray empty (nothing is pre-picked there).
      var serverPrefill =
        serverCur && serverCur.available && (!this.data.oneMode || (this.data.prefillSets && serverOffer !== 'one'));
      this.trayKey =
        serverOffer +
        '|' +
        (serverPrefill ? String(this.data.current) : '') +
        '|' +
        (serverOffer === 'four' ? serverFree : '');
      if (serverFree) this.freeKey = serverFree;
    }

    bind() {
      var self = this;
      this.bound = true;
      this.onClick = this.handleClick.bind(this);
      this.onChange = this.handleChange.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);

      // A press on the CTA: Dawn's form submit on the product page, the section's own buttons.
      var commit = function (submitter) {
        self.reconcile();
        self.commit(submitter);
      };
      this.unbindCommit = this.host.bindCommit(this, commit);
      this.unbindBuyNow = this.host.bindBuyNow(this, commit);

      this.onPageShow = function (event) {
        if (event && event.persisted) {
          self.navigating = false;
          self.added = false;
          // A page restored from the back/forward cache keeps its last announcement.
          self.clearLive();
          self.setBusy(false);
        }
        self.reconcile();
        self.render();
      };
      window.addEventListener('pageshow', this.onPageShow);

      this.onCartState = function (event) {
        var cart = event && event.detail && event.detail.cart;
        if (cart && typeof cart.item_count === 'number') {
          self.cartCount = cart.item_count;
          self.renderFine();
        }
      };
      document.addEventListener('lpfg:state', this.onCartState);

      // The theme editor selects the block (product page) or the section.
      this.selectEvent = this.host.selectEvent;
      this.onBlockSelect = function (event) {
        if (!self.host.isSelected(self, event)) return;
        (self.root || self).scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
      };
      document.addEventListener(this.selectEvent, this.onBlockSelect);

      this.unsubscribeVariant = this.host.subscribeVariant(this);

      this.bindReach();
      this.bindBar();
    }

    unbind() {
      if (!this.bound) return;
      this.bound = false;
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      if (typeof this.unbindCommit === 'function') this.unbindCommit();
      if (typeof this.unbindBuyNow === 'function') this.unbindBuyNow();
      window.removeEventListener('pageshow', this.onPageShow);
      document.removeEventListener('lpfg:state', this.onCartState);
      document.removeEventListener(this.selectEvent, this.onBlockSelect);
      if (typeof this.unsubscribeVariant === 'function') this.unsubscribeVariant();
      if (this.ctaObserver) this.ctaObserver.disconnect();
      if (this.seenObserver) this.seenObserver.disconnect();
      if (this.offersObserver) this.offersObserver.disconnect();
      this.offersObserver = null;
      if (this.reachObserver) this.reachObserver.disconnect();
      if (this.host.needsReach) setFlag(document.documentElement, 'data-mf-builder-reached', false);
      if (this.mobileQuery && this.onMobile) {
        if (this.mobileQuery.removeEventListener) this.mobileQuery.removeEventListener('change', this.onMobile);
        else if (this.mobileQuery.removeListener) this.mobileQuery.removeListener(this.onMobile);
      }
      if (this.bar && this.onBarClick) this.bar.removeEventListener('click', this.onBarClick);
    }

    /* ---------- the bar: visibility and its own controls ---------- */
    bindBar() {
      var self = this;
      var bar = this.bar;
      if (!bar) return;
      this.mobileQuery = window.matchMedia ? window.matchMedia('(max-width: 749px)') : null;
      this.onMobile = function () {
        // Back on a phone: the header may be shorter than when the offers observer was made.
        if (self.mobileQuery.matches) self.observeOffers();
        self.renderBarVisibility();
      };
      if (this.mobileQuery) {
        if (this.mobileQuery.addEventListener) this.mobileQuery.addEventListener('change', this.onMobile);
        else if (this.mobileQuery.addListener) this.mobileQuery.addListener(this.onMobile);
      }
      if ('IntersectionObserver' in window && this.button) {
        // This form's own Add to cart, not the page's first product form.
        this.ctaObserver = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              self.ctaVisible = entry.isIntersecting;
            });
            self.renderBarVisibility();
          },
          { rootMargin: '0px 0px -40px 0px', threshold: 0.05 }
        );
        this.ctaObserver.observe(this.button);
        // Teaser until a quarter of the builder has been on screen once (60% of the screen on a
        // short landscape screen, where a quarter of the builder can never fit).
        this.seenObserver = new IntersectionObserver(
          function (entries) {
            var seen = entries.some(function (entry) {
              if (!entry.isIntersecting) return false;
              var viewport = entry.rootBounds ? entry.rootBounds.height : window.innerHeight;
              var need = Math.min(entry.boundingClientRect.height * 0.25, viewport * 0.6);
              return entry.intersectionRect.height >= need;
            });
            if (!seen) return;
            self.seen = true;
            self.seenObserver.disconnect();
            self.renderBar();
          },
          { threshold: [0, 0.05, 0.1, 0.15, 0.2, 0.25] }
        );
        this.seenObserver.observe(this);
        this.observeOffers();
      }
      this.onBarClick = function (event) {
        var variantButton = event.target.closest && event.target.closest('[data-bar-variant]');
        if (variantButton) {
          var picker = self.info && self.info.querySelector('variant-selects');
          if (!picker) return;
          picker.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'center' });
          var tile = picker.querySelector('input:checked');
          if (tile) tile.focus({ preventScroll: true });
          return;
        }
        if (event.target.closest && event.target.closest('[data-bar-cta]')) self.clearError();
      };
      bar.addEventListener('click', this.onBarClick);
    }

    // A section's bar also stays away while the offer cards are on screen. The strip under the
    // sticky header is not on screen: the top of the observed area starts below the header, so
    // cards scrolled up under it hand over to the bar. Remade only when the header height changes.
    observeOffers() {
      var self = this;
      if (!this.host.needsReach || !this.els.offers || !('IntersectionObserver' in window)) return;
      var top = stickyHeaderHeight();
      if (this.offersObserver) {
        if (this.offersTop === top) return;
        this.offersObserver.disconnect();
      }
      this.offersTop = top;
      this.offersObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            self.offersVisible = entry.isIntersecting;
          });
          self.renderBarVisibility();
        },
        { rootMargin: (top ? -top : 0) + 'px 0px -40px 0px', threshold: 0.05 }
      );
      this.offersObserver.observe(this.els.offers);
    }

    // A section: "reached" while the builder is on screen or scrolled past (live, not latched). It
    // writes html[data-mf-builder-reached], so the landing page's own Shop Now bar hands over to
    // this bar from the builder down and comes back above it (sections/lp-car-perfume.liquid).
    bindReach() {
      var self = this;
      if (!this.host.needsReach || !this.root || !('IntersectionObserver' in window)) return;
      this.reachObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            self.reached = entry.isIntersecting || entry.boundingClientRect.top < 0;
          });
          setFlag(document.documentElement, 'data-mf-builder-reached', self.reached);
          self.renderBarVisibility();
        },
        { threshold: 0 }
      );
      this.reachObserver.observe(this.root);
    }

    renderBarVisibility() {
      var bar = this.bar;
      if (!bar) return;
      var mobile = this.mobileQuery ? this.mobileQuery.matches : false;
      var hide = !mobile || this.ctaVisible;
      if (this.host.needsReach) hide = hide || this.offersVisible || !this.reached;
      if (hide && !bar.hidden && bar.contains(document.activeElement) && this.button) {
        try {
          this.button.focus({ preventScroll: true });
        } catch (e) {}
      }
      bar.hidden = hide;
      setFlag(bar, 'inert', hide);
      setAria(bar, 'aria-hidden', hide ? 'true' : null);
    }

    /* ---------- input ---------- */
    handleClick(event) {
      var target = event.target;
      if (!target || !target.closest) return;
      var step = target.closest('[data-step]');
      if (step) {
        this.clearError();
        var card = step.closest('[data-variant-id]');
        if (!card || step.disabled) return;
        var id = card.getAttribute('data-variant-id');
        if (step.getAttribute('data-step') === '1') this.plus(id, step);
        else this.minus(id, step);
        return;
      }
      // A tap anywhere on the card outside its stepper (photo, scent name, design and price) works as
      // its own "+": each tap adds one more until the set is full. "+" and "−" stay the card's only
      // controls for keyboards and screen readers.
      var faceCard = target.closest('.monfiora-scent');
      if (faceCard && !target.closest('.monfiora-stepper')) {
        var faceId = faceCard.getAttribute('data-variant-id');
        if (!faceId || this.cards[faceId] !== faceCard) return;
        var facePlus = faceCard.querySelector('[data-step="1"]');
        if (!facePlus || facePlus.disabled) return;
        this.clearError();
        this.plus(faceId, facePlus);
        return;
      }
      var toggle = target.closest('[data-group-toggle]');
      if (toggle) {
        var open = toggle.getAttribute('aria-expanded') !== 'true';
        toggle.setAttribute('aria-expanded', String(open));
        var grid = document.getElementById(toggle.getAttribute('aria-controls'));
        if (grid) {
          grid.hidden = !open;
          if (open) this.animateOnce(grid, 'is-opening');
        }
        return;
      }
      if (target.closest('[data-free-toggle]')) {
        this.toggleFree();
        return;
      }
      if (target.closest('[data-undo]')) {
        this.clearError();
        this.undo();
      }
    }

    handleChange(event) {
      var input = event.target;
      if (!input || input.type !== 'radio') return;
      if (input.name === 'mf-offer-' + this.sid) {
        this.clearError();
        this.chooseOffer(input.value);
      } else if (input.name === 'mf-free-' + this.sid) {
        this.clearError();
        this.chooseFree(input.value);
      }
    }

    plus(id, button) {
      if (this.busy) return;
      var s = this.state;
      if (button && button.getAttribute('aria-disabled') === 'true') {
        if (s.option !== 'one' && s.pickLog.length >= Core.OFFERS[s.option].target) this.announce(this.text.helperFull);
        return;
      }
      var result = Core.addPick(this.V, s, id);
      var v = this.V[id];
      if (s.option === 'one' && s.oneMode) {
        // Single choice: a new pick replaces the old one, and says so.
        if (result !== 'added' && result !== 'replaced') return;
        this.announce(Core.fill(this.text.liveOne, { scent: v.scent, design: v.design }));
        this.render();
        return;
      }
      if (result === 'full') this.announce(this.text.helperFull);
      if (result !== 'added') return;
      var q = Core.quote(this.V, s.option, s);
      this.announce(
        q.complete
          ? Core.fill(this.text.liveReady, { t: q.target })
          : Core.fill(this.text.liveAdded, { scent: v.scent, design: v.design, n: q.count, t: q.target })
      );
      this.render();
    }

    minus(id, button) {
      if (this.busy || (button && button.getAttribute('aria-disabled') === 'true')) return;
      var s = this.state;
      if (Core.removePick(s, id) !== 'removed') return;
      var q = Core.quote(this.V, s.option, s);
      var v = this.V[id];
      if (s.option === 'one' && s.oneMode) this.announce(Core.fill(this.text.liveOneRemoved, { scent: v.scent, design: v.design }));
      else this.announce(Core.fill(this.text.liveRemoved, { scent: v.scent, design: v.design, n: q.count, t: q.target }));
      this.render();
    }

    chooseOffer(option) {
      var s = this.state;
      if (!Core.OFFERS[option] || option === s.option) return;
      if (option === 'four' && !this.freeAvailable()) {
        this.render();
        return;
      }
      // The set picker appears on the product page; a section's picker is already open in Just one,
      // where the counter is said as for a set ("0 of 1 picked", "1 of 1 picked. Ready to add.").
      var fromOne = s.option === 'one' && !s.oneMode;
      var trimmed = Core.switchOffer(s, option);
      if (fromOne) this.animateOnce(this, 'monfiora-builder--reveal');
      if (option !== 'one' || s.oneMode) {
        var q = Core.quote(this.V, option, s);
        if (trimmed && trimmed.length) {
          this.announce(Core.fill(this.text.liveTrim, { name: this.nameOf(trimmed[0]), n: q.count, t: q.target }));
        } else {
          this.announce(q.complete ? Core.fill(this.text.liveReady, { t: q.target }) : Core.fill(this.text.count, { n: q.count, t: q.target }));
        }
      }
      this.render();
    }

    chooseFree(id) {
      if (!Core.isAvailable(this.V, id)) return;
      this.state.freeId = String(id);
      this.announce(Core.fill(this.text.liveFree, { name: this.nameOf(id) }));
      this.render();
    }

    undo() {
      if (!Core.undoTrim(this.state)) return;
      this.render();
      var four = this.radios.four;
      if (four) {
        try {
          four.focus({ preventScroll: true });
        } catch (e) {}
      }
      var q = Core.quote(this.V, this.state.option, this.state);
      this.announce(q.complete ? Core.fill(this.text.liveReady, { t: q.target }) : Core.fill(this.text.count, { n: q.count, t: q.target }));
    }

    toggleFree() {
      var toggle = this.els.freeToggle;
      var panel = this.els.freePanel;
      if (!toggle || !panel) return;
      var open = toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', String(open));
      panel.hidden = !open;
      setText(toggle, open ? this.text.freeDone : this.text.freeChange);
    }

    onVariant(variant) {
      var id = String(variant.id);
      if (this.V[id] && typeof variant.available === 'boolean') this.V[id].available = variant.available;
      Core.moveCurrent(this.V, this.state, id);
      this.render();
    }

    // The checked radios decide, whatever the browser restored without a change event.
    reconcile() {
      var s = this.state;
      if (!s) return;
      var checked = this.querySelector('input[name="mf-offer-' + this.sid + '"]:checked');
      if (checked && !checked.disabled && checked.value !== s.option && Core.OFFERS[checked.value]) Core.switchOffer(s, checked.value);
      var free = this.freeInputs.filter(function (i) {
        return i.checked;
      })[0];
      if (free && String(free.value) !== s.freeId && Core.isAvailable(this.V, free.value)) s.freeId = String(free.value);
    }

    /* ---------- commit: add, or show what is missing ---------- */
    commit(submitter) {
      if (this.busy || this.navigating) return;
      this.clearError();
      var s = this.state;
      var q = Core.quote(this.V, s.option, s);
      if (!q.complete) {
        if (s.option !== 'one' || s.oneMode) this.guide();
        return;
      }
      if (s.option === 'four' && !Core.isAvailable(this.V, s.freeId)) return;
      this.add(submitter, q);
    }

    // An incomplete set: bring the picker heading into view and say what is missing.
    guide() {
      var label = this.els.pickLabel;
      var q = Core.quote(this.V, this.state.option, this.state);
      var r = q.target - q.count;
      if (label) {
        label.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
        try {
          label.focus({ preventScroll: true });
        } catch (e) {}
      }
      if (this.state.option === 'one') this.announce(this.text.liveOneIncomplete);
      // "more" is wrong while nothing is picked yet: say the helper's own empty-set line instead.
      else if (!q.count) this.announce(Core.fill(this.text.helperNone, { t: q.target }));
      else this.announce(r === 1 ? this.text.liveGuideOne : Core.fill(this.text.liveGuide, { r: r }));
    }

    sectionIds() {
      var mc = window.MonfioraCart;
      if (mc && typeof mc.sectionIds === 'function') {
        try {
          var ids = mc.sectionIds();
          return Array.isArray(ids) ? ids : [];
        } catch (e) {
          return [];
        }
      }
      var drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        return drawer.getSectionsToRender().map(function (section) {
          return section.id;
        });
      }
      return [];
    }

    add(submitter, q) {
      var self = this;
      var s = this.state;
      var option = s.option;
      var setId = option === 'four' ? Core.newSetId() : null;
      var items = Core.buildItems(s, setId);
      var ctx = { option: option, items: items, setId: setId, expected: q.total };
      var fg = window.MonfioraFreeGift;
      var before = fg && typeof fg.getCart === 'function' ? fg.getCart() : null;
      var sections = this.sectionIds();
      var body = { items: items };
      if (sections.length) {
        body.sections = sections;
        body.sections_url = window.location.pathname;
      }
      var opener = submitter && submitter.isConnected ? submitter : this.button;
      this.setBusy(true);
      // Returned so the outcome can be awaited (tests); the page never waits on it.
      return Core.submit(
        function (url, init) {
          return window.fetch(url, init);
        },
        (this.data.cartAddUrl || '/cart/add') + '.js',
        body
      ).then(function (outcome) {
        var step = Core.nextStep(outcome);
        if (step === 'success') return self.succeeded(outcome.json, ctx, opener);
        if (step === 'committed') return self.committed(ctx, opener, null);
        if (step === 'refused') return self.refused(outcome.json, ctx);
        // A 5xx can arrive after Shopify saved the set: the cart decides, never a blind "tap again".
        if (step === 'check') return self.lost(ctx, before, opener, outcome.status);
        self.setBusy(false);
        log({ type: outcome.type, status: outcome.status, option: option }, true);
        self.showError(self.text.errAdd);
      });
    }

    succeeded(json, ctx, opener) {
      this.setBusy(false);
      emit('monfiora:builder:added', { option: ctx.option, items: ctx.items, setId: ctx.setId, expected: ctx.expected });
      this.openDrawer(json, opener, this.drawerOpts(ctx));
      this.afterAdd();
      this.verify(ctx, null);
    }

    // Shopify committed the add but its response could not be read (or a lost response was found in
    // the cart): show the cart from the server. `message` replaces the drawer's own announcement.
    committed(ctx, opener, cart, message) {
      this.setBusy(false);
      emit('monfiora:builder:added', { option: ctx.option, items: ctx.items, setId: ctx.setId, expected: ctx.expected });
      var opts = this.drawerOpts(ctx);
      if (message) opts.message = message;
      this.refreshDrawer(opener, opts);
      this.afterAdd();
      this.verify(ctx, cart);
    }

    // A set is announced once as "Set added to your cart." even when it is one line (3 × one scent);
    // the drawer would otherwise infer it from the number of lines.
    drawerOpts(ctx) {
      return ctx && ctx.option !== 'one' ? { kind: 'set' } : {};
    }

    afterAdd() {
      var self = this;
      Core.resetPicks(this.V, this.state);
      // The picks are back to the pre-fill: "3 of 3 picked. Ready to add." must not stay behind in
      // the counter's live region (the drawer announces the add).
      this.clearLive();
      this.added = true;
      clearTimeout(this.addedTimer);
      this.addedTimer = setTimeout(function () {
        self.added = false;
        self.render();
      }, 1000);
      this.render();
    }

    refused(json, ctx) {
      var self = this;
      var V = this.V;
      var s = this.state;
      var ids = ctx.items.map(function (i) {
        return String(i.id);
      });
      var description = (json && (json.description || json.message)) || '';
      log({ type: 'refused', option: ctx.option, description: String(description).slice(0, 200) }, true);
      var hits = Core.matchSoldOut(V, description, ids);
      var limit = Core.parseLimit(description);
      if (limit && hits.length) {
        // Limited stock, not sold out: nothing was added and nothing changes; say how many fit.
        var lv = V[hits[0]];
        this.clearLive();
        this.setBusy(false);
        this.showAlert(Core.fill(this.text.errLimited, { n: limit, design: lv.design, scent: lv.scent }));
        this.render();
        this.revealAlert();
        return;
      }
      var found = hits.length
        ? Promise.resolve(hits)
        : Core.findSoldOut(V, ids, function (url) {
            return Core.getJson(windowFetch, url + '.js', READ_TIMEOUT);
          });
      found
        .catch(function () {
          return [];
        })
        .then(function (soldOut) {
          // The alert explains the refusal; a pick announcement made before the tap would now
          // contradict the counter.
          self.clearLive();
          self.setBusy(false);
          if (!soldOut || !soldOut.length) {
            self.showAlert(self.text.errSoldOutUnknown);
            self.render();
            self.revealAlert();
            return;
          }
          var res = Core.markSoldOut(V, s, self.pool, soldOut);
          var messages = [];
          var named = res.paid[0] || (soldOut.indexOf(String(s.current)) !== -1 ? String(s.current) : null);
          if (named) messages.push(Core.fill(self.text.errSoldOut, { name: self.nameOf(named) }));
          if (res.free && res.free.to) {
            messages.push(Core.fill(self.text.errFree, { name: self.nameOf(res.free.from), name2: self.nameOf(res.free.to) }));
          } else if (res.free) {
            messages.push(self.text.fourSoldOut);
          }
          if (!messages.length) messages.push(self.text.errSoldOutUnknown);
          if (s.option === 'four' && !self.freeAvailable()) Core.switchOffer(s, 'three');
          self.showAlert(messages.join(' '));
          self.render();
          self.revealAlert();
        });
    }

    // No usable answer to the add (no response, or a 5xx / status-0 reply): read the cart and let it
    // decide. `status` is only logged (0 = no response at all).
    lost(ctx, before, opener, status) {
      var self = this;
      return this.readCart().then(function (after) {
        var verdict = Core.resolveLost({ option: ctx.option, items: ctx.items, setId: ctx.setId, before: before, after: after });
        log({ type: 'lost_response', option: ctx.option, status: Number(status) || 0, verdict: verdict }, verdict !== 'absent');
        if (verdict === 'found') {
          // Said once, by the drawer that takes focus (without one the page goes to the cart).
          self.committed(ctx, opener, after, self.text.lostFound);
          return;
        }
        self.setBusy(false);
        if (verdict === 'absent') {
          self.showError(self.text.errAdd);
          return;
        }
        // Unknown: the set may be in the cart, so the bar must not invite a second tap with
        // "tap again"; it carries the lead of the same line.
        self.showError(self.text.lost, self.text.barLost || null);
        self.refreshDrawer(opener, {});
      });
    }

    readCart() {
      return Core.getJson(windowFetch, (this.data.cartUrl || '/cart') + '.js', READ_TIMEOUT).then(function (cart) {
        return cart && Array.isArray(cart.items) ? cart : null;
      });
    }

    // Compare the cart with the button total (rule: only when the cart holds exactly this set).
    verify(ctx, cart) {
      var self = this;
      var fg = window.MonfioraFreeGift;
      var read;
      if (cart) read = Promise.resolve(cart);
      else if (fg && typeof fg.nextState === 'function') {
        read = Promise.resolve(fg.nextState(4000)).then(function (snap) {
          return snap && snap.cart ? snap.cart : self.readCart();
        });
      } else read = this.readCart();
      read
        .catch(function () {
          return null;
        })
        .then(function (fresh) {
          var r = Core.checkCart(ctx.items, fresh, ctx.expected, ctx.setId, ctx.option);
          var problem = r.result === 'mismatch' || r.notes.length > 0;
          log({ type: 'check', result: r.result, option: ctx.option, expected: r.expected, actual: r.actual, notes: r.notes }, problem);
          emit('monfiora:builder:check', { result: r.result, expected: r.expected, actual: r.actual });
          if (r.result !== 'mismatch') return;
          var mc = window.MonfioraCart;
          if (mc && typeof mc.note === 'function') {
            try {
              mc.note(Core.fill(self.text.mismatch, { total: self.money(r.actual) }));
            } catch (e) {}
          }
        });
    }

    openDrawer(json, opener, opts) {
      var mc = window.MonfioraCart;
      if (mc && typeof mc.openDrawer === 'function') {
        try {
          this.watchDrawer(mc.openDrawer(json, Object.assign({ opener: opener }, opts)), 'openDrawer');
          return;
        } catch (e) {
          console.warn('[monfiora-builder]', e);
        }
      }
      var drawer = document.querySelector('cart-drawer');
      if (drawer && json && json.sections && typeof drawer.renderContents === 'function') {
        try {
          if (opener && typeof drawer.setActiveElement === 'function') drawer.setActiveElement(opener);
          drawer.renderContents(json);
          return;
        } catch (e) {
          console.warn('[monfiora-builder]', e);
        }
      }
      this.leaveTo(this.data.cartUrl || '/cart');
    }

    refreshDrawer(opener, opts) {
      var mc = window.MonfioraCart;
      if (mc && typeof mc.refresh === 'function') {
        try {
          this.watchDrawer(mc.refresh(Object.assign({ open: true, opener: opener }, opts)), 'refresh');
          return;
        } catch (e) {
          console.warn('[monfiora-builder]', e);
        }
      }
      this.leaveTo(this.data.cartUrl || '/cart');
    }

    // The drawer calls return promises that reject when its sections read fails: the drawer then never
    // opened, so the cart page shows what happened instead (and nothing is left unhandled).
    watchDrawer(result, call) {
      var self = this;
      if (!result || typeof result.then !== 'function') return;
      result.then(null, function (e) {
        log({ type: 'drawer_failed', call: call, message: String((e && e.message) || e).slice(0, 120) }, true);
        self.leaveTo(self.data.cartUrl || '/cart');
      });
    }

    leaveTo(url) {
      this.navigating = true;
      window.location.href = url;
    }

    setBusy(on) {
      this.busy = !!on;
      setAria(this, 'aria-busy', on ? 'true' : null);
      setFlag(this.els.offers, 'inert', on);
      setFlag(this.els.picker, 'inert', on);
      setFlag(this.els.free, 'inert', on);
      this.render();
    }

    /* ---------- messages ---------- */
    announce(message) {
      var self = this;
      var live = this.els.live;
      if (!live || !message) return;
      clearTimeout(this.liveTimer);
      clearTimeout(this.liveRepeat);
      this.liveTimer = setTimeout(function () {
        if (live.textContent === message) {
          live.textContent = '';
          self.liveRepeat = setTimeout(function () {
            live.textContent = message;
          }, 50);
        } else {
          live.textContent = message;
        }
      }, 400);
    }

    // Empty the counter's live region and drop any announcement still waiting to land, so it never
    // reads a state the counter no longer shows.
    clearLive() {
      clearTimeout(this.liveTimer);
      clearTimeout(this.liveRepeat);
      setText(this.els && this.els.live, '');
    }

    showAlert(message) {
      setText(this.els.alert, message || '');
    }

    // A refusal explained in the builder (a sold-out pick) is brought on screen when the customer
    // could not see it, e.g. after adding from the bar at 390 with the builder scrolled away.
    revealAlert() {
      var el = this.els.alert;
      if (!el || !el.textContent || typeof el.getBoundingClientRect !== 'function') return;
      var rect = el.getBoundingClientRect();
      if (!rect.height) return;
      var bar = this.bar && !this.bar.hidden ? this.bar.getBoundingClientRect().height : 0;
      if (rect.top >= 0 && rect.bottom <= window.innerHeight - bar) return;
      el.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'center' });
    }

    // Under the buy row (Dawn's Add to cart) on the product page, in the checkout block's alert line
    // in a section; the bar repeats it while it is showing.
    // barText: the bar's line (default "Couldn't add — tap again."), null for none.
    showError(message, barText) {
      this.clearError();
      this.host.showError(this, message);
      this.barError = barText === undefined ? this.text.barError : barText || null;
      this.renderBar();
    }

    clearError() {
      this.host.clearError(this);
      this.showAlert('');
      if (this.barError) {
        this.barError = null;
        this.renderBar();
      }
    }

    /* ---------- helpers ---------- */
    // A one-off entrance (opacity, never height); skipped by the CSS under reduced motion.
    animateOnce(el, cls) {
      if (!el || !el.classList) return;
      var done = function () {
        el.classList.remove(cls);
        el.removeEventListener('animationend', done);
      };
      el.classList.remove(cls);
      var start = function () {
        el.classList.add(cls);
        el.addEventListener('animationend', done);
        setTimeout(done, 600);
      };
      if (typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(start);
      else start();
    }

    money(paise) {
      return Core.formatMoney(paise, this.data.moneyFormat, this.data.zeroLabel);
    }

    moneyEl(paise, cls, tag) {
      return span(cls || 'monfiora-builder__money', this.money(paise), tag);
    }

    nameOf(id) {
      var v = this.V[String(id)];
      return v ? v.design + ' · ' + v.scent : '';
    }

    freeAvailable() {
      return !!Core.firstAvailable(this.V, this.pool);
    }

    // The lowest price a section's Just one can add (the picker's scents, from the payload's order).
    fromPrice() {
      var order = this.data && Array.isArray(this.data.order) && this.data.order.length ? this.data.order : Object.keys(this.V);
      return Core.fromPrice(this.V, order);
    }

    buyNowHost(event) {
      var path = typeof event.composedPath === 'function' ? event.composedPath() : [];
      for (var i = 0; i < path.length; i++) {
        var node = path[i];
        if (node && node.nodeType === 1 && node.matches && node.matches(BUY_NOW_HOSTS)) return node;
      }
      var target = event.target && event.target.nodeType === 1 ? event.target : null;
      return target && target.closest ? target.closest(BUY_NOW_HOSTS) : null;
    }

    imageFor(id) {
      var card = this.cards[String(id)];
      var img = card && card.querySelector('img');
      if (!img) {
        var input = this.querySelector('#MfFree-' + id + '-' + this.sid);
        var label = input && input.nextElementSibling;
        img = label && label.querySelector('img');
      }
      if (!img) return null;
      var copy = img.cloneNode(false);
      copy.removeAttribute('id');
      copy.setAttribute('alt', '');
      copy.setAttribute('sizes', '56px');
      return copy;
    }

    /* ============================== render ============================== */
    render() {
      if (!this.state) return;
      var s = this.state;
      var V = this.V;
      var setOffer = s.option !== 'one';
      // A section's Just one opens the same picker with a target of 1.
      var showPicker = setOffer || !!s.oneMode;
      this.setAttribute('data-offer', s.option);
      if (this.info) {
        if (this.info.getAttribute('data-builder-option') !== 'off') this.info.setAttribute('data-builder-option', s.option);
        setFlag(this.info, 'data-builder-soldout', !Core.isAvailable(V, s.current));
      }
      if (this.root && this.root.getAttribute('data-builder-option') !== 'off') this.root.setAttribute('data-builder-option', s.option);
      if (this.els.picker) this.els.picker.hidden = !showPicker;
      if (this.els.free) this.els.free.hidden = !(s.option === 'four' && s.freeId);
      // A section's sets start empty: no "Total so far" until the first pick.
      if (this.els.summary) this.els.summary.hidden = !setOffer || (!!s.oneMode && !s.pickLog.length);
      this.renderOffers();
      this.renderFine();
      if (showPicker) {
        this.renderStatus();
        this.renderCards();
        this.renderNotice();
      }
      if (setOffer) this.renderSummary();
      this.renderFree();
      this.renderButton();
      this.renderBar();
    }

    renderOffers() {
      var self = this;
      var s = this.state;
      var V = this.V;
      var freeOk = this.freeAvailable();
      OPTIONS.forEach(function (opt) {
        var radio = self.radios[opt];
        var label = self.labels[opt];
        if (radio) {
          if (radio.checked !== (s.option === opt)) radio.checked = s.option === opt;
          if (opt === 'four') radio.disabled = !freeOk;
        }
        if (!label) return;
        var q = Core.quote(V, opt, s);
        // A section's Just one with nothing picked: "From" the lowest price in stock, no saving.
        var from = opt === 'one' && !!s.oneMode && s.oneId == null;
        setText(label.querySelector('[data-option-price]'), from ? Core.fill(self.text.oneFrom, { money: self.money(self.fromPrice()) }) : self.money(q.total));
        var save = label.querySelector('[data-option-save]');
        var showSave = q.save > 0 && !from;
        // Just one's saving follows the scent it prices: a section's own pick, else the landing scent.
        var priced = s.oneMode ? s.oneId : s.current;
        if (opt === 'one' && !Core.isAvailable(V, priced)) showSave = false;
        if (opt === 'four' && !freeOk) showSave = false;
        if (save) {
          save.hidden = !showSave;
          if (showSave) {
            var regular = span('visually-hidden', self.text.regular);
            var mrp = self.moneyEl(q.mrpTotal, 'monfiora-option__money', 's');
            var amount = self.moneyEl(q.save, 'monfiora-option__money');
            put(save, '{regular}{mrp} ' + self.text.save.replace('{money}', '{amount}'), { regular: regular, mrp: mrp, amount: amount });
          } else if (opt === 'one' && s.oneMode) {
            // Emptied too: the radio's aria-describedby would still read a hidden, stale saving.
            setText(save, '');
            save.__mfText = '';
          }
        }
        var sub = label.querySelector('[data-option-sub]');
        if (opt === 'one' && sub && s.oneMode) {
          // A section: the card follows Just one's own pick.
          var pick = s.oneId != null ? V[String(s.oneId)] : null;
          if (pick) put(sub, '{item}', { item: nameSpan('monfiora-option__item', Core.fill(self.text.oneSub, { design: pick.design, scent: pick.scent })) });
          else put(sub, self.text.oneSubNone, {});
        } else if (opt === 'one' && sub) {
          var cur = V[String(s.current)];
          if (cur && cur.available) {
            put(sub, '{item}', { item: nameSpan('monfiora-option__item', Core.fill(self.text.oneSub, { design: cur.design, scent: cur.scent })) });
          } else if (cur) {
            put(sub, Core.fill(self.text.oneSoldOut, { scent: cur.scent }), {});
          }
        }
        if (opt === 'four' && sub) setText(sub, freeOk ? self.fourSub : self.text.fourSoldOut || self.fourSub);
      });
    }

    renderFine() {
      var el = this.els.fine;
      if (!el) return;
      var n = this.cartCount;
      setText(el, n >= 2 ? Core.fill(this.text.cartMany, { n: n }) : n === 1 ? this.text.cartOne : this.text.fine);
    }

    renderStatus() {
      var s = this.state;
      var V = this.V;
      var q = Core.quote(V, s.option, s);
      var r = q.target - q.count;
      if (s.oneMode) {
        setText(this.els.pickLabel, s.option === 'one' ? this.text.pickHeadingOne : this.text.pickHeading);
        setText(this.els.railLabel, s.option === 'one' ? this.text.railOne : this.text.railSet);
      }
      // The counter, in the picker and the section's rail (a section's Just one: "0 of 1 picked").
      var countText = Core.fill(this.text.count, { n: q.count, t: q.target });
      (this.els.countTexts || []).forEach(function (el) {
        setText(el, countText);
      });
      (this.els.countChecks || []).forEach(function (el) {
        el.hidden = !q.complete;
      });
      if (s.option === 'one') {
        // A section's Just one: the helper, the pick by name in the rail and the one-slot tray.
        var pick = s.oneId != null ? V[String(s.oneId)] : null;
        if (pick) put(this.els.helper, this.text.helperOne, { scent: nameSpan('monfiora-builder__helper-name', pick.scent) });
        else put(this.els.helper, this.text.helperOneNone, {});
        if (this.els.railPick) {
          this.els.railPick.hidden = !pick;
          setText(this.els.railPickName, pick ? Core.fill(this.text.oneSub, { design: pick.design, scent: pick.scent }) : '');
        }
        this.renderTray();
        return;
      }

      // Helper
      var cur = V[String(s.current)];
      if (q.complete) put(this.els.helper, this.text.helperFull, {});
      else if (s.prefillPristine && cur && cur.available && s.pickLog.length === 1 && s.pickLog[0] === String(s.current)) {
        // Off the product page nobody chose the stand-in, so it is "in to start", not "your first pick".
        put(this.els.helper, s.oneMode ? this.text.helperFirstStandalone : this.text.helperFirst, {
          scent: nameSpan('monfiora-builder__helper-name', cur.scent),
          r: r
        });
      } else if (s.prefillPristine && cur && !cur.available && !s.pickLog.length) {
        put(this.els.helper, this.text.helperSoldOut, { scent: cur.scent, t: q.target });
      } else if (!s.pickLog.length) put(this.els.helper, this.text.helperNone, { t: q.target });
      else put(this.els.helper, this.text.helperMore, { r: r });

      this.renderTray();
    }

    // What the tray holds: a section's Just one pick (one slot), else the set's picks.
    trayIds() {
      var s = this.state;
      if (s.option === 'one' && s.oneMode) return s.oneId != null ? [String(s.oneId)] : [];
      return s.pickLog.slice(0, Core.OFFERS[s.option].target);
    }

    // Every [data-tray] gets the same slots (a section's rail mirrors the picker's tray).
    renderTray() {
      var self = this;
      var s = this.state;
      var trays = this.els.trays || [];
      if (!trays.length) return;
      var offer = Core.OFFERS[s.option];
      var withFree = offer.free && s.freeId;
      var ids = this.trayIds();
      var key = s.option + '|' + ids.join(',') + '|' + (withFree ? s.freeId : '');
      if (this.trayKey === key) return;
      this.trayKey = key;
      trays.forEach(function (tray) {
        var frag = self.traySlots(offer, withFree, ids);
        while (tray.firstChild) tray.removeChild(tray.firstChild);
        tray.appendChild(frag);
      });
    }

    traySlots(offer, withFree, ids) {
      var s = this.state;
      var frag = document.createDocumentFragment();
      for (var i = 0; i < offer.target; i++) {
        var id = ids[i];
        var li = document.createElement('li');
        var v = id ? this.V[id] : null;
        if (v) {
          li.className = 'monfiora-builder__slot monfiora-builder__slot--filled';
          li.appendChild(span('visually-hidden', Core.fill(this.text.slotFilled, { scent: v.scent, design: v.design })));
          var img = this.imageFor(id);
          if (img) {
            img.className = 'monfiora-builder__slot-img';
            li.appendChild(img);
          }
        } else {
          li.className = 'monfiora-builder__slot monfiora-builder__slot--open';
          var num = span('monfiora-builder__slot-num', String(i + 1));
          num.setAttribute('aria-hidden', 'true');
          li.appendChild(num);
          li.appendChild(span('visually-hidden', this.text.slotOpen));
        }
        frag.appendChild(li);
      }
      if (withFree) {
        var plus = span('monfiora-builder__slot-plus', '+', 'li');
        plus.setAttribute('aria-hidden', 'true');
        frag.appendChild(plus);
        var fv = this.V[s.freeId];
        var free = document.createElement('li');
        free.className = 'monfiora-builder__slot monfiora-builder__slot--free';
        free.appendChild(span('visually-hidden', Core.fill(this.text.slotFree, { scent: fv ? fv.scent : '', design: fv ? fv.design : '' })));
        var fimg = this.imageFor(s.freeId);
        if (fimg) {
          fimg.className = 'monfiora-builder__slot-img';
          free.appendChild(fimg);
        }
        var flag = span('monfiora-builder__slot-flag', this.text.freeFlag);
        flag.setAttribute('aria-hidden', 'true');
        free.appendChild(flag);
        frag.appendChild(free);
      }
      return frag;
    }

    renderCards() {
      var self = this;
      var s = this.state;
      var V = this.V;
      var counts = Core.pickCounts(s);
      // A section's Just one: "+" stays live on every other scent (it replaces the pick).
      var single = s.option === 'one' && !!s.oneMode;
      var atTarget = !single && s.pickLog.length >= Core.OFFERS[s.option].target;
      var perDesign = {};
      Object.keys(this.cards).forEach(function (id) {
        var card = self.cards[id];
        var v = V[id];
        if (!v) return;
        var qty = counts[id] || 0;
        perDesign[v.h] = (perDesign[v.h] || 0) + qty;
        var full = single ? qty >= 1 : atTarget;
        var key = qty + '|' + v.available + '|' + full;
        if (card.__mfKey === key) return;
        card.__mfKey = key;
        card.classList.toggle('monfiora-scent--picked', qty > 0);
        card.classList.toggle('monfiora-scent--soldout', !v.available);
        var minus = card.querySelector('[data-step="-1"]');
        var plus = card.querySelector('[data-step="1"]');
        [minus, plus].forEach(function (b) {
          if (!b) return;
          b.disabled = !v.available;
        });
        setAria(minus, 'aria-disabled', v.available && qty === 0 ? 'true' : null);
        setAria(plus, 'aria-disabled', v.available && full ? 'true' : null);
        setText(card.querySelector('[data-qty]'), String(qty));
        var suffix = (qty ? Core.fill(self.text.inSet, { n: qty }) : '') + (v.available ? '' : self.text.soldOutSuffix);
        setText(card.querySelector('[data-scent-suffix]'), suffix);
        if (!v.available && !card.querySelector('.monfiora-scent__soldout')) {
          var media = card.querySelector('.monfiora-scent__media');
          var strip = span('monfiora-scent__soldout', self.text.soldOut || '');
          strip.setAttribute('aria-hidden', 'true');
          if (media && strip.textContent) media.appendChild(strip);
        }
      });
      this.groups.forEach(function (group) {
        var chip = group.querySelector('[data-group-chip]');
        var n = perDesign[group.getAttribute('data-design')] || 0;
        if (!chip) return;
        chip.hidden = n === 0;
        setText(chip, Core.fill(self.text.rowPicked, { n: n }));
      });
    }

    renderNotice() {
      var s = this.state;
      var notice = this.els.notice;
      if (!notice) return;
      if (!s.trim || !s.trim.ids.length) {
        notice.hidden = true;
        return;
      }
      put(this.els.noticeText, this.text.trim, { name: nameSpan('monfiora-builder__notice-name', this.nameOf(s.trim.ids[0])) });
      notice.hidden = false;
    }

    renderSummary() {
      var s = this.state;
      var q = Core.quote(this.V, s.option, s);
      if (this.els.sofarRow) this.els.sofarRow.hidden = q.complete;
      if (this.els.totals) this.els.totals.hidden = !q.complete;
      if (!q.complete) {
        setText(this.els.sofar, this.money(q.picked));
        return;
      }
      setText(this.els.totalMrp, this.money(q.mrpTotal));
      setText(this.els.total, this.money(q.total));
      setText(this.els.totalSave, this.money(q.save));
      if (s.option === 'three') put(this.els.totalIncl, this.text.inclThree, {});
      else {
        put(this.els.totalIncl, this.text.inclFour, {
          name: nameSpan('monfiora-builder__incl-name', this.nameOf(s.freeId)),
          zero: this.moneyEl(0)
        });
      }
    }

    renderFree() {
      var self = this;
      var s = this.state;
      var V = this.V;
      this.freeInputs.forEach(function (input) {
        var id = String(input.value);
        var v = V[id];
        var available = !!(v && v.available);
        if (input.disabled !== !available) input.disabled = !available;
        var checked = id === s.freeId;
        if (input.checked !== checked) input.checked = checked;
        var label = input.nextElementSibling;
        if (label && label.classList) label.classList.toggle('monfiora-freetile--soldout', !available);
      });
      if (!s.freeId || this.freeKey === s.freeId) return;
      this.freeKey = s.freeId;
      var v = V[s.freeId];
      if (!v) return;
      setText(this.els.freeName, v.scent);
      setText(this.els.freeDesign, v.design);
      setText(this.els.freeMrp, this.money(Core.mrpOf(v)));
      var thumb = this.els.freeThumb;
      var img = this.imageFor(s.freeId);
      if (thumb && img) {
        img.className = 'monfiora-freepick__img';
        while (thumb.firstChild) thumb.removeChild(thumb.firstChild);
        thumb.appendChild(img);
      }
    }

    // The CTA (Dawn's Add to cart, or a section's own): label in its first span, the amount in the
    // price slot (text-transform: none, so the currency never renders in capitals).
    renderButton() {
      var b = this.button;
      if (!b) return;
      var s = this.state;
      var q = Core.quote(this.V, s.option, s);
      var label;
      var price = '';
      var ariaDisabled = false;
      var disabled = false;
      if (this.busy) {
        label = this.text.ctaAdding;
        ariaDisabled = true;
      } else if (this.added) {
        label = this.text.ctaAdded;
      } else if (s.option === 'one') {
        if (q.complete) {
          label = this.text.ctaAdd;
          price = this.money(q.total);
        } else if (s.oneMode) {
          label = this.text.ctaStartOne;
          ariaDisabled = true;
        } else {
          label = this.text.ctaSoldOut;
          disabled = true;
        }
      } else if (q.complete) {
        label = this.text.ctaSet;
        price = this.money(q.total);
      } else {
        var r = q.target - q.count;
        // "more" is wrong while nothing is picked yet.
        if (!q.count) label = Core.fill(this.text.ctaStart, { t: q.target });
        else label = r === 1 ? this.text.ctaMoreOne : Core.fill(this.text.ctaMore, { r: r });
        ariaDisabled = true;
      }
      setText(b.querySelector('span'), label);
      var slot = b.querySelector(PRICE_SLOT);
      if (slot) {
        setText(slot, price);
        slot.hidden = !price;
      }
      if (b.disabled !== disabled) b.disabled = disabled;
      setAria(b, 'aria-disabled', ariaDisabled ? 'true' : null);
      setAria(b, 'aria-busy', this.busy ? 'true' : null);
      // A section's CTA is described by the summary only while it shows: a hidden element named in
      // aria-describedby is still read out (an empty "Total so far" under Just one's "Add to cart").
      if (this.host.kind === 'section') {
        var summary = this.els.summary;
        setAria(b, 'aria-describedby', summary && summary.id && !summary.hidden ? summary.id : null);
      }
      b.classList.toggle('loading', this.busy);
      var spinner = b.querySelector('.loading__spinner');
      if (spinner) spinner.classList.toggle('hidden', !this.busy);
    }

    renderBar() {
      var bar = this.bar;
      if (!bar || !this.state) return;
      var s = this.state;
      var q = Core.quote(this.V, s.option, s);
      var textOne = this.host.barOne === 'text';
      var state;
      if (s.option === 'one') state = 'one';
      else if (q.complete) state = 'ready';
      else if (s.prefillPristine && !this.seen) state = 'teaser';
      // A section's empty set reads as the teaser, never "0 of 4 picked".
      else if (s.oneMode && !q.count) state = 'teaser';
      else state = 'progress';
      if (bar.getAttribute('data-state') !== state) bar.setAttribute('data-state', state);

      var fillEl = bar.querySelector('[data-bar-fill]');
      if (fillEl) {
        var ratio = state === 'progress' || state === 'ready' ? Math.min(1, q.count / q.target) : 0;
        var scale = 'scaleX(' + Math.round(ratio * 1000) / 1000 + ')';
        if (fillEl.style.transform !== scale) fillEl.style.transform = scale;
      }
      var text = bar.querySelector('[data-bar-text]');
      var variant = bar.querySelector('[data-bar-variant]');
      // Just one: the "Fragrance:" button on the product page, the offer name and the pick in a section.
      if (text) text.hidden = state === 'one' && !textOne;
      if (variant) variant.hidden = state !== 'one';
      var l1 = bar.querySelector('[data-bar-l1]');
      var l2 = bar.querySelector('[data-bar-l2]');
      var cur = this.V[String(s.current)];
      var r = q.target - q.count;
      var showError = !!this.barError && (state !== 'one' || textOne);
      if (l2) l2.classList.toggle('monfiora-builder-bar__l2--error', showError);
      if (state === 'one' && textOne) {
        // A section's Just one: the offer name over "0 of 1 picked", then over the pick.
        var pick = s.oneId != null ? this.V[String(s.oneId)] : null;
        setText(l1, this.text.titleOne);
        if (showError) put(l2, this.barError, {});
        else if (pick) put(l2, this.text.oneSub, { design: pick.design, scent: pick.scent });
        else put(l2, this.text.count, { n: q.count, t: q.target });
      } else if (state === 'teaser') {
        setText(l1, s.option === 'three' ? this.text.titleThree : this.text.titleFour);
        if (showError) put(l2, this.barError, {});
        else if (cur && cur.available && s.pickLog.length) put(l2, this.text.barTeaser, { scent: cur.scent, r: r });
        else put(l2, this.text.barTeaserEmpty, { t: q.target });
      } else if (state === 'progress') {
        setText(l1, Core.fill(this.text.count, { n: q.count, t: q.target }));
        if (showError) put(l2, this.barError, {});
        else put(l2, this.text.barSoFar, { money: this.moneyEl(q.picked) });
      } else if (state === 'ready') {
        setText(l1, Core.fill(s.option === 'three' ? this.text.barReadyThree : this.text.barReadyFour, { t: q.target }));
        if (showError) put(l2, this.barError, {});
        else {
          // No struck MRP beside it here, so the saving says what it is measured against.
          var saveText = span('monfiora-builder-bar__save');
          put(saveText, ' · ' + this.text.saveMrp, { money: this.moneyEl(q.save) });
          put(l2, '{total}{save}', { total: this.moneyEl(q.total), save: saveText });
        }
      }
      if (variant && cur) setText(variant.querySelector('[data-bar-variant-value]'), cur.scent);

      var cta = bar.querySelector('[data-bar-cta]');
      if (!cta) return;
      var label;
      var price = '';
      var ariaDisabled = false;
      var disabled = false;
      if (state === 'teaser') label = this.text.barChoose;
      else if (state === 'progress') {
        label = Core.fill(this.text.barPick, { r: r });
        ariaDisabled = true;
      } else if (state === 'ready') label = this.text.barAddSet;
      else if (q.complete) {
        label = this.text.ctaAdd;
        price = this.money(q.total);
      } else if (s.oneMode) {
        label = this.text.ctaStartOne;
        ariaDisabled = true;
      } else {
        label = this.text.ctaSoldOut;
        disabled = true;
      }
      if (this.busy) {
        label = this.text.ctaAdding;
        ariaDisabled = true;
      }
      setText(cta.querySelector('[data-bar-label]'), label);
      var priceEl = cta.querySelector('[data-bar-price]');
      if (priceEl) {
        setText(priceEl, price);
        priceEl.hidden = !price || this.busy;
      }
      cta.classList.toggle('monfiora-builder-bar__cta--two-line', !!price);
      if (cta.disabled !== disabled) cta.disabled = disabled;
      setAria(cta, 'aria-disabled', ariaDisabled ? 'true' : null);
      setAria(cta, 'aria-busy', this.busy ? 'true' : null);
      cta.classList.toggle('loading', this.busy);
      var spinner = cta.querySelector('.loading__spinner');
      if (spinner) spinner.classList.toggle('hidden', !this.busy);
    }
  }

  /* ============================== <monfiora-builder-bar> ============================== */
  // Display only: <monfiora-builder> drives it. Inside a quick-add modal it removes itself.
  class MonfioraBuilderBar extends HTMLElement {
    connectedCallback() {
      if (this.closest('quick-add-modal')) this.remove();
    }
  }

  if (!customElements.get('monfiora-builder-bar')) customElements.define('monfiora-builder-bar', MonfioraBuilderBar);
  if (!customElements.get('monfiora-builder')) customElements.define('monfiora-builder', MonfioraBuilder);
})();
